import React, { useRef, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import videoManifest from '../../utils/videoManifest.json';

/**
 * ResponsiveVideo Component
 *
 * Automatically renders optimized video sources (WebM + MP4) with appropriate
 * resolutions and poster images based on the video manifest.
 *
 * Usage:
 * <ResponsiveVideo
 *   src="/assets/yorkies/video-name.mp4"  // Path to original video
 *   alt="Description"
 *   linkToFullRes={true}  // Optional: wrap in anchor for full-res download/viewing
 *   clickToPause={true}  // Optional: enable click to play/pause (default: true when controls=true)
 *   autoPlay
 *   loop
 *   muted
 *   playsInline
 * />
 */
const ResponsiveVideo = ({
    src,
    alt,
    className,
    style,
    autoPlay,
    loop,
    muted,
    playsInline,
    controls = true,
    linkToFullRes = false,
    clickToPause = true,
    ...props
}) => {
    const videoRef = useRef(null);
    const [isInView, setIsInView] = useState(false);

    // Lazy load video - only initialize when in viewport
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        // Once in view, stop observing
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '50px', // Start loading 50px before entering viewport
            }
        );

        observer.observe(videoElement);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Memoize video data lookup - only recalculate when src changes
    const videoData = useMemo(() => {
        // Extract folder and video name from src
        // e.g., "/assets/yorkies/video.mp4" -> folder: "yorkies", name: "video"
        const pathParts = src.replace('/assets/', '').split('/');
        const fileName = pathParts[pathParts.length - 1].replace(/\.(mp4|webm)$/, '');
        const folder = pathParts.slice(0, -1).join('/');

        // Find the video in the manifest
        const folderVideos = videoManifest[folder];
        if (!folderVideos) return null;

        return folderVideos.find(v => v.name === fileName);
    }, [src]);

    const handleVideoClick = (e) => {
        if (clickToPause && videoRef.current) {
            // Toggle play/pause when clicking directly on the video
            if (videoRef.current.paused) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    };

    // Fallback if video not found in manifest
    if (!videoData) {
        console.warn(`ResponsiveVideo: Video "${src}" not found in videoManifest.json`);
        // Fallback to basic video tag
        return (
            <video
                ref={videoRef}
                src={src}
                className={className}
                style={style}
                controls={controls}
                autoPlay={autoPlay}
                loop={loop}
                muted={muted}
                playsInline={playsInline}
                onClick={handleVideoClick}
                {...props}
            >
                Your browser does not support the video tag.
            </video>
        );
    }

    // Memoize resolution check and source sorting - only recalculate when videoData changes
    const { hasMultipleResolutions, webmSources, mp4Sources } = useMemo(() => {
        const uniqueResolutions = [...new Set(videoData.sources.map(s => s.resolution))];

        return {
            hasMultipleResolutions: uniqueResolutions.length > 1,
            webmSources: videoData.sources
                .filter(source => source.type === 'video/webm')
                .sort((a, b) => parseInt(b.resolution) - parseInt(a.resolution)),
            mp4Sources: videoData.sources
                .filter(source => source.type === 'video/mp4')
                .sort((a, b) => parseInt(b.resolution) - parseInt(a.resolution))
        };
    }, [videoData]);

    const videoElement = (
        <video
            ref={videoRef}
            poster={videoData.poster}
            className={linkToFullRes ? undefined : className}
            style={linkToFullRes ? { width: '100%', height: 'auto', display: 'block' } : style}
            controls={controls}
            autoPlay={isInView && autoPlay} // Only autoplay when in viewport
            loop={loop}
            muted={muted}
            playsInline={playsInline}
            aria-label={alt}
            onClick={handleVideoClick}
            {...props}
        >
            {/* Only load sources when video is in viewport - prevents blocking */}
            {isInView && (
                <>
                    {/* WebM sources - better compression, served first */}
                    {webmSources.map((source, index) => (
                        <source
                            key={`webm-${index}`}
                            src={source.src}
                            type={source.type}
                            media={hasMultipleResolutions && source.resolution === '720' ? '(max-width: 800px)' : undefined}
                        />
                    ))}

                    {/* MP4 fallback sources */}
                    {mp4Sources.map((source, index) => (
                        <source
                            key={`mp4-${index}`}
                            src={source.src}
                            type={source.type}
                            media={hasMultipleResolutions && source.resolution === '720' ? '(max-width: 800px)' : undefined}
                        />
                    ))}
                </>
            )}

            Your browser does not support the video tag.
        </video>
    );

    // Wrap in anchor for full-res download/viewing if enabled
    // The anchor allows right-click "Save video as..." and "Open in new tab"
    // Left-click is prevented so video controls work normally (play/pause)
    if (linkToFullRes) {
        return (
            <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
                style={{
                    ...style,
                    display: 'inline-block',
                    lineHeight: 0,
                    textDecoration: 'none',
                    color: 'inherit',
                }}
                onClick={(e) => {
                    // Prevent left-click navigation so video controls work
                    // Right-click context menu still works for "Open in new tab" / "Save as"
                    e.preventDefault();
                }}
            >
                {videoElement}
            </a>
        );
    }

    return videoElement;
};

ResponsiveVideo.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool,
    muted: PropTypes.bool,
    playsInline: PropTypes.bool,
    controls: PropTypes.bool,
    linkToFullRes: PropTypes.bool,
    clickToPause: PropTypes.bool,
};

export default ResponsiveVideo;
